import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { notification } from 'components/ds';
import PageContainer from '../../../components/PageContainer/PageContainer';
import { pageConfigs } from '../../../configs/pageConfigs';
import { useOtp } from '../../../hooks/useOtp';
import { useSearchUser } from '../../../hooks/useSearchUser';
import {
  TwoFactorValidationCodeComponent
} from '../../../components/TwoFactorValidationCodeComponent/TwoFactorValidationCodeComponent';
import { FactorValidationWrapper } from '../ForgotPasswordOtpRequest/ForgotPasswordOtpRequest.styles';
import { useOtpContext } from '../../../context/OtpContextProvider';
import { useGetUserData } from '../../../hooks/useGetUserData';
import { ApiLocalStorage } from 'utils/ApiLocalStorage';
import { useSchema } from '../../../hooks/useSchema';
import { SIGNUP_TYPES } from '../../../_types/schema.types';
import { useParticipantId } from '../../../hooks/useParticipantId';
import { getNavigationDestination } from '../../../utils/getNavigationDestination';
import { useUsername } from '../../../hooks/useUsername';
import { useAuth } from '../../../../../auth/AuthProvider';
import { USER_DATA_KEY } from '../../../constantes/LocalStorageEnum';
import { ValidatorField } from '../../../../../components/ValidatorField';
import { ToogleWhatsappResend } from 'utils/toggleWhatsappResend';
import { getChannelValue } from '../../../utils/getChannelValue';

export const OtpValidation: React.FC = () => {
  const [code, setCode] = useState('');
  const [isResendDisabled, setIsResendDisabled] = useState<boolean>(true);
  const navigate = useNavigate();

  const { getUserData: fetchUserData, loading: userDataLoading } = useGetUserData();
  const { userData } = useSearchUser();
  const {
    validateOtpCode,
    loading: validateCodeLoading,
    sendOtpRequest,
    otpId,
  } = useOtp();
  const { selectedChannel, currentFlow } = useOtpContext();
  const { signupType } = useSchema();
  const { participantId } = useParticipantId();
  const { username } = useUsername();
  const { loginWithOtp } = useAuth();

  const isOpenFlow = signupType === SIGNUP_TYPES.OPEN;

  const handleValidateOtpCode = async () => {
    if (!otpId) {
      console.error('Erro no validateOtpCode:: otpId');
      notification.error({
        message: 'Erro na validação',
        description: 'Ocorreu um erro ao validar o código. Por favor, verifique o código inserido e tente novamente.',
      });
      return;
    }

    if (!participantId) {
      console.error('Erro no validateOtpCode:: participantId');
      notification.error({
        message: 'Erro',
        description: 'Dados do usuário não encontrados. Por favor, faça login novamente.',
      });
      return;
    }

    const otpResponse = await validateOtpCode({
      otpId,
      code,
      flow: currentFlow || 'Authentication',
      generateToken: true,
      participantId,
      signUpType: signupType,
    });

    if (otpResponse && otpResponse.validCode) {
      ToogleWhatsappResend(false);

      console.log('OTP validado com sucesso:', {
        isOpenFlow,
        currentFlow,
        hasAccessToken: !!otpResponse.accessToken,
        hasExpiresIn: !!otpResponse.expiresIn,
        username,
      });

      let loginSuccess = true;

      try {
        // Sempre buscar os dados do usuário via /account/me/data
        const response = await fetchUserData();
        ApiLocalStorage.set(USER_DATA_KEY, response);

        // Fazer login automático caso exista accessToken e username
        if (otpResponse.accessToken && username) {
          try {
            const cleanedUsername = ValidatorField.removeSpecialCharsFromUsername(username);
            const loginResult = await loginWithOtp(
              cleanedUsername,
              otpResponse.accessToken,
              otpResponse.expiresIn || '3600',
              response
            );

            loginSuccess = true;
          } catch (error) {
            notification.error({
              message: 'Erro',
              description: 'Não foi possível fazer login automático, mas o registro foi concluído.',
            });
            loginSuccess = true; // Não bloqueia o fluxo
          }
        }
      } catch (error) {
        notification.error({
          message: 'Erro',
          description: 'Não foi possível buscar os dados do usuário.',
        });
        loginSuccess = false;
      }

      if (loginSuccess) {
        // Verifica se é um caso especial de recuperação de onboarding
        const isOnboardingRecovery =
          !isOpenFlow &&
          currentFlow === 'Authentication' &&
          userData?.userFound &&
          !userData?.firstAccess &&
          !userData?.onboardingFinished;

        const destination = getNavigationDestination({
          isOpenFlow,
          currentFlow: currentFlow || '',
          isOnboardingRecovery,
        });

        // Aguardar breve delay antes da navegação
        await new Promise((resolve) => setTimeout(resolve, 1000));
        navigate(destination);
      } else {
        console.error('Login não foi bem-sucedido, não navegando');
      }
    }
  };

  const handleResend = async () => {
    if (!participantId || !currentFlow) {
      notification.error({
        message: 'Erro',
        description: 'Dados do usuário não encontrados. Por favor, faça login novamente.',
      });
      return;
    }

    try {
      const request = {
        participantId,
        flow: currentFlow,
        channel: selectedChannel,
      };

      await sendOtpRequest(request);
      setIsResendDisabled(true);
    } catch (error) {
      notification.error({
        message: 'Erro ao reenviar código',
        description: 'Não foi possível reenviar o código. Por favor, tente novamente.',
      });
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.slice(0, 6);
    setCode(value);
  };

  const channelValue = getChannelValue(selectedChannel, userData);

  const codeLength = 6;
  const isLoading = userDataLoading || validateCodeLoading;

  return (
    <PageContainer
      title={pageConfigs.validateOtpResetPassword.title}
      navigateBack={pageConfigs.validateOtpResetPassword.navigateBack()}
      buttonText={pageConfigs.validateOtpResetPassword.buttonLabel}
      thereIsButton
      loading={isLoading}
      onClick={handleValidateOtpCode}
      disabled={isLoading || code.length !== codeLength}
    >
      <FactorValidationWrapper>
        <TwoFactorValidationCodeComponent
          code={code}
          setIsResendDisabled={setIsResendDisabled}
          isResendDisabled={isResendDisabled}
          channelValue={channelValue}
          onCodeChange={handleInputChange}
          onSubmit={handleValidateOtpCode}
          onResend={handleResend}
        />
      </FactorValidationWrapper>
    </PageContainer>
  );
};
